import React, { useState, useEffect } from "react";
import { formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { Trash2, RefreshCw, Download, Search, AlertTriangle } from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import { fmtDateTimeLong } from "../utils/dateUtils";

// Inline API calls (light module)
import axios from "axios";
const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("manna_access_token")}` } });

const TYPE_COLORS = {
  "Purchase Order": "bg-blue-100 text-blue-700",
  "Route": "bg-[#E8956D]/15 text-[#E8956D]",
  "User": "bg-purple-100 text-purple-700",
  "Vendor": "bg-green-100 text-green-700",
  "Stock Inward": "bg-amber-100 text-amber-700",
  "Stock Issue": "bg-red-100 text-red-700",
};

export default function DeletionLog() {
  const { kitchens, selectedKitchen } = useKitchen();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => { fetchStats(); fetchLogs(); }, []);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/api/deletion-logs/stats`, auth());
      setStats(r.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append("record_type", filterType);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      if (selectedKitchen) params.append("kitchen_id", selectedKitchen);
      params.append("limit", "500");
      const r = await axios.get(`${API}/api/deletion-logs?${params}`, auth());
      setLogs(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const filteredLogs = logs.filter(l => {
    if (!search) return true;
    return (
      l.description?.toLowerCase().includes(search.toLowerCase()) ||
      l.deleted_by?.toLowerCase().includes(search.toLowerCase()) ||
      l.record_type?.toLowerCase().includes(search.toLowerCase())
    );
  });

  const recordTypes = [...new Set(logs.map(l => l.record_type).filter(Boolean))].sort();

  const handleExport = () => {
    exportCSV(filteredLogs.map(l => ({
      date_time: fmtDateTimeLong(l.deleted_at),
      record_type: l.record_type,
      description: l.description,
      deleted_by: l.deleted_by,
      deleted_by_email: l.deleted_by_email,
      kitchen: l.kitchen_id || "—",
    })), `deletion_log_${fromDate}_${toDate}`);
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="deletion-log">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C] flex items-center gap-3">
            <span className="w-10 h-10 bg-[#FBEAE7] rounded-xl flex items-center justify-center">
              <Trash2 size={20} className="text-[#C2624E]" />
            </span>
            Deletion Log
          </h1>
          <p className="text-sm text-[#8A7E73] mt-0.5 ml-14">Audit trail of all deleted records across all modules</p>
        </div>
        <button onClick={handleExport} disabled={filteredLogs.length === 0}
          className="flex items-center gap-2 border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] disabled:opacity-40">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 col-span-2 md:col-span-1">
            <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Total Deletions</p>
            <p className="text-3xl font-extrabold text-[#C2624E]">{stats.total}</p>
          </div>
          {(stats.by_type || []).slice(0, 3).map(t => (
            <div key={t.type} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
              <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1 truncate">{t.type}</p>
              <p className="text-2xl font-extrabold text-[#3A322C]">{t.count}</p>
            </div>
          ))}
        </div>
      )}

      {/* Most active deleters */}
      {stats?.by_user?.length > 0 && (
        <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
          <p className="text-xs font-bold uppercase text-[#8A7E73] mb-3">Deletions by User</p>
          <div className="flex flex-wrap gap-2">
            {stats.by_user.map(u => (
              <span key={u.user} className="text-xs bg-[#F4F3F0] text-[#5C5147] px-3 py-1.5 rounded-full font-medium">
                {u.user || "Unknown"} <span className="font-bold text-[#C2624E]">({u.count})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-48 relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
          <input
            placeholder="Search by description, deleted by..."
            className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={search} onChange={e => setSearch(e.target.value)}
            data-testid="deletion-log-search"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Record Type</label>
          <select className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            {recordTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">From</label>
          <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">To</label>
          <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={toDate} onChange={e => setToDate(e.target.value)} />
        </div>
        <button onClick={() => { fetchStats(); fetchLogs(); }} disabled={loading}
          className="flex items-center gap-2 bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-60"
          data-testid="deletion-log-filter-btn">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />} Apply
        </button>
      </div>

      {/* Log Table */}
      {loading ? (
        <div className="text-center py-12"><RefreshCw size={24} className="animate-spin text-[#E8956D] mx-auto" /></div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E1DB] rounded-2xl">
          <Trash2 size={40} className="mx-auto mb-3 text-[#C4BAAF]" />
          <p className="font-bold text-[#3A322C]">No deletions recorded</p>
          <p className="text-sm text-[#8A7E73] mt-1">Deletions will appear here as records are removed from the system</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#F4F3F0] flex items-center justify-between">
            <p className="text-sm font-bold text-[#3A322C]">
              {filteredLogs.length} deletion{filteredLogs.length !== 1 ? "s" : ""}
            </p>
            {filteredLogs.length > 0 && (
              <p className="text-xs text-[#8A7E73]">Most recent first</p>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F3F0]">
                <tr>
                  {["Date & Time", "Type", "What Was Deleted", "Deleted By", "Kitchen"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wide text-[#5C5147] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F4F3F0]">
                {filteredLogs.map((log, i) => (
                  <tr key={log.id || i} className="hover:bg-[#FAFAF8]" data-testid={`deletion-log-row-${i}`}>
                    <td className="px-4 py-3 text-xs text-[#8A7E73] whitespace-nowrap">
                      {fmtDateTimeLong(log.deleted_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TYPE_COLORS[log.record_type] || "bg-gray-100 text-gray-600"}`}>
                        {log.record_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#3A322C] max-w-xs">
                      <p className="text-sm truncate">{log.description || "—"}</p>
                      <p className="text-xs text-[#C4BAAF] font-mono mt-0.5">ID: {log.record_id?.slice(-8)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#3A322C] text-sm">{log.deleted_by || "—"}</p>
                      <p className="text-xs text-[#8A7E73]">{log.deleted_by_email || ""}</p>
                    </td>
                    <td className="px-4 py-3">
                      {log.kitchen_id ? (
                        <span className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-2 py-0.5 rounded-full font-medium">
                          {kitchens.find(k => k.id === log.kitchen_id)?.name || log.kitchen_id}
                        </span>
                      ) : <span className="text-xs text-[#C4BAAF]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info note */}
      <div className="flex items-start gap-3 bg-[#FDF4EB] border border-[#E8956D]/20 rounded-xl p-4">
        <AlertTriangle size={16} className="text-[#D98032] mt-0.5 shrink-0" />
        <p className="text-xs text-[#5C5147]">
          <strong>Note:</strong> This log covers deletions made through the app. 
          Records logged: Purchase Orders, Routes, Vendors, Users. 
          Stock and HR records are soft-deleted and can be recovered by an admin.
        </p>
      </div>
    </div>
  );
}
